import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  IoWarningOutline,
  IoLocationOutline,
  IoTimeOutline,
  IoDocumentTextOutline,
  IoChevronForward,
  IoAddCircleOutline,
  IoEyeOutline,
} from "react-icons/io5";
import { Dialog, DialogFooter } from "../ui/dialog";
import { BadgeStatus } from "../BadgeStatus";
import { getStatusColor } from "@/utils/getStatusColor";
import { convertStatusToLabel } from "@/utils/convertStatusToLabel";
import { formatDateTime } from "@/utils/formatDateTime";
import type { PotentialDuplicate } from "@/types/DuplicateOcurrence";

interface DuplicateAlertProps {
  isOpen: boolean;
  onClose: () => void;
  duplicates: PotentialDuplicate[];
  onFollowOcurrence: (occurrenceId: string) => void;
  onContinueReport: () => void;
  isFollowing?: boolean;
}

export const DuplicateAlert = ({
  isOpen,
  onClose,
  duplicates,
  onFollowOcurrence,
  onContinueReport,
  isFollowing = false,
}: DuplicateAlertProps) => {
  const navigate = useNavigate();
  const [selectedDuplicate, setSelectedDuplicate] =
    useState<PotentialDuplicate | null>(null);

  const formatDistance = (meters: number): string => {
    if (meters < 1000) {
      return `${Math.round(meters)}m`;
    }
    return `${(meters / 1000).toFixed(1)}km`;
  };

  const handleFollowClick = (duplicate: PotentialDuplicate) => {
    setSelectedDuplicate(duplicate);
  };

  
  const confirmFollow = () => {
    if (selectedDuplicate) {
      onFollowOcurrence(selectedDuplicate.id);
    }
  };

  const handleViewOcurrence = (occurrenceId: string) => {
    navigate(`/ocurrences/details/${occurrenceId}`);
  };

  // Modal de confirmação para acompanhar
  if (selectedDuplicate) {
    return (
      <Dialog isOpen={isOpen} onClose={() => setSelectedDuplicate(null)}>
        <div className="text-center py-2">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <IoEyeOutline className="text-3xl text-blue-600" />
          </div>

          <h3 className="text-lg font-bold text-gray-800 mb-2">
            Acompanhar ocorrência?
          </h3>

          <p className="text-sm text-gray-600 mb-4">
            Você será vinculado como interessado e receberá todas as
            notificações sobre o andamento desta ocorrência.
          </p>

          <div className="bg-gray-50 rounded-xl p-4 mb-4 text-left">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">
                Protocolo
              </span>
              <span className="text-sm font-mono text-[hsl(var(--primary))]">
                #{selectedDuplicate.protocol}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Status atual</span>
              <BadgeStatus
                title={convertStatusToLabel(selectedDuplicate.status)}
                color={getStatusColor(selectedDuplicate.status)}
              />
            </div>
          </div>

          <DialogFooter>
            <button
              onClick={() => setSelectedDuplicate(null)}
              className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              Voltar
            </button>
            <button
              onClick={confirmFollow}
              disabled={isFollowing}
              className="flex-1 py-3 px-4 rounded-xl bg-[hsl(var(--primary))] text-white font-semibold hover:bg-[hsl(var(--primary))]/90 transition-colors disabled:opacity-50"
            >
              {isFollowing ? "Processando..." : "Confirmar"}
            </button>
          </DialogFooter>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog isOpen={isOpen} onClose={onClose} showCloseButton={false}>
      <div className="py-2">
        {/* Header com ícone de alerta */}
        <div className="text-center mb-5">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <IoWarningOutline className="text-3xl text-yellow-600" />
          </div>

          <h3 className="text-lg font-bold text-gray-800 mb-2">
            Identificamos uma ocorrência similar nesta região
          </h3>

          <p className="text-sm text-gray-600">
            {duplicates.length === 1
              ? "Encontramos um registro que pode estar relacionado ao problema que você deseja reportar."
              : `Encontramos ${duplicates.length} registros que podem estar relacionados ao problema que você deseja reportar.`}
          </p>
        </div>

        {/* Lista de duplicatas */}
        <div className="space-y-3 max-h-64 overflow-y-auto mb-5">
          {duplicates.map((duplicate, index) => (
            <motion.div
              key={duplicate.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-gray-50 rounded-xl p-4 border border-gray-100"
            >
              {/* Protocolo e Status */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <IoDocumentTextOutline className="text-gray-500" />
                  <span className="font-mono font-semibold text-sm text-gray-800">
                    #{duplicate.protocol}
                  </span>
                </div>
                <BadgeStatus
                  title={convertStatusToLabel(duplicate.status)}
                  color={getStatusColor(duplicate.status)}
                />
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <IoTimeOutline className="text-gray-400" />
                  <span>{formatDateTime(duplicate.createdAt)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <IoLocationOutline className="text-gray-400" />
                  <span>~{formatDistance(duplicate.distanceMeters)}</span>
                </div>
              </div>

              {/* Categoria */}
              <p className="text-xs text-gray-500 mb-3">
                {duplicate.category.name} → {duplicate.subcategory.name}
              </p>

              {/* Ações */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleViewOcurrence(duplicate.id)}
                  className="flex-1 py-2 px-3 rounded-lg bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors flex items-center justify-center gap-1"
                >
                  <IoEyeOutline />
                  Ver detalhes
                </button>
                <button
                  onClick={() => handleFollowClick(duplicate)}
                  className="flex-1 py-2 px-3 rounded-lg bg-[hsl(var(--primary))] text-sm font-medium text-white hover:bg-[hsl(var(--primary))]/90 transition-colors flex items-center justify-center gap-1"
                >
                  Acompanhar
                  <IoChevronForward />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Opção de continuar com novo registro (RN-004) */}
        <div className="border-t border-gray-100 pt-4">
          <button
            onClick={onContinueReport}
            className="w-full py-3 px-4 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 font-medium hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5 transition-all flex items-center justify-center gap-2"
          >
            <IoAddCircleOutline className="text-xl" />
            Registrar nova ocorrência mesmo assim
          </button>
          <p className="text-xs text-center text-gray-400 mt-2">
            Você pode registrar uma nova ocorrência se acredita que este é um
            problema diferente
          </p>
        </div>
      </div>
    </Dialog>
  );
};
